import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster, toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { LoginPage } from "@/components/portal/LoginPage";
import { PortalShell } from "@/components/portal/PortalShell";
import { ModuleAccessGuard } from "@/components/portal/ModuleAccessGuard";
import { api } from "@/lib/api";
import { PLACEHOLDER_ROUTE_IDS, getDefaultLandingPath, getRouteById } from "@/lib/portalModules";
import { Sentry, captureFrontendException, initFrontendMonitoring, sentryEnabled } from "@/lib/monitoring";

const lazyNamed = (loader, exportName) => lazy(() => loader().then((module) => ({ default: module[exportName] })));

const OverviewPage = lazyNamed(() => import("@/components/portal/OverviewPage"), "OverviewPage");
const VenUsOperationsPage = lazyNamed(() => import("@/components/portal/VenUsOperationsPage"), "VenUsOperationsPage");
const ScheduleHubPage = lazyNamed(() => import("@/components/portal/ScheduleHubPage"), "ScheduleHubPage");
const SharedChatPage = lazyNamed(() => import("@/components/portal/SharedChatPage"), "SharedChatPage");
const FinancePage = lazyNamed(() => import("@/components/portal/FinancePage"), "FinancePage");
const PayrollPage = lazyNamed(() => import("@/components/portal/PayrollPage"), "PayrollPage");
const HRPage = lazyNamed(() => import("@/components/portal/HRPage"), "HRPage");
const HREmployeesPage = lazyNamed(() => import("@/components/portal/HREmployeesPage"), "HREmployeesPage");
const HRHiringPage = lazyNamed(() => import("@/components/portal/HRHiringPage"), "HRHiringPage");
const HRRequisitionsPage = lazyNamed(() => import("@/components/portal/HRRequisitionsPage"), "HRRequisitionsPage");
const HRRequisitionDetailPage = lazyNamed(() => import("@/components/portal/HRRequisitionDetailPage"), "HRRequisitionDetailPage");
const HRCandidatesPage = lazyNamed(() => import("@/components/portal/HRCandidatesPage"), "HRCandidatesPage");
const HRCandidateDetailPage = lazyNamed(() => import("@/components/portal/HRCandidateDetailPage"), "HRCandidateDetailPage");
const HROnboardingPage = lazyNamed(() => import("@/components/portal/HROnboardingPage"), "HROnboardingPage");
const HROnboardingDetailPage = lazyNamed(() => import("@/components/portal/HROnboardingDetailPage"), "HROnboardingDetailPage");
const HRExitsPage = lazyNamed(() => import("@/components/portal/HRExitsPage"), "HRExitsPage");
const HRExitDetailPage = lazyNamed(() => import("@/components/portal/HRExitDetailPage"), "HRExitDetailPage");
const HRAssetsPage = lazyNamed(() => import("@/components/portal/HRAssetsPage"), "HRAssetsPage");
const HRAssetDetailPage = lazyNamed(() => import("@/components/portal/HRAssetDetailPage"), "HRAssetDetailPage");
const HRReviewsPage = lazyNamed(() => import("@/components/portal/HRReviewsPage"), "HRReviewsPage");
const HRReviewDetailPage = lazyNamed(() => import("@/components/portal/HRReviewDetailPage"), "HRReviewDetailPage");
const HRAnalyticsPage = lazyNamed(() => import("@/components/portal/HRAnalyticsPage"), "HRAnalyticsPage");
const AcquisitionPage = lazyNamed(() => import("@/components/portal/AcquisitionPage"), "AcquisitionPage");
const AcquisitionPipelinePage = lazyNamed(() => import("@/components/portal/AcquisitionPipelinePage"), "AcquisitionPipelinePage");
const AcquisitionBriefPage = lazyNamed(() => import("@/components/portal/AcquisitionBriefPage"), "AcquisitionBriefPage");
const AcquisitionVisitPrepPage = lazyNamed(() => import("@/components/portal/AcquisitionVisitPrepPage"), "AcquisitionVisitPrepPage");
const AcquisitionFieldCapturePage = lazyNamed(() => import("@/components/portal/AcquisitionFieldCapturePage"), "AcquisitionFieldCapturePage");
const AcquisitionReviewPage = lazyNamed(() => import("@/components/portal/AcquisitionReviewPage"), "AcquisitionReviewPage");
const RmPage = lazyNamed(() => import("@/components/portal/RmPage"), "RmPage");
const RmPipelinePage = lazyNamed(() => import("@/components/portal/RmPipelinePage"), "RmPipelinePage");
const RmMobileDeskPage = lazyNamed(() => import("@/components/portal/RmMobileDeskPage"), "RmMobileDeskPage");
const OwnerOnboardingPage = lazyNamed(() => import("@/components/portal/OwnerOnboardingPage"), "OwnerOnboardingPage");
const VendorsPage = lazyNamed(() => import("@/components/portal/VendorsPage"), "VendorsPage");
const VenueVendorConnectionsPage = lazyNamed(() => import("@/components/portal/VenueVendorConnectionsPage"), "VenueVendorConnectionsPage");
const VendorsDirectoryPage = lazyNamed(() => import("@/components/portal/VendorsDirectoryPage"), "VendorsDirectoryPage");
const VendorPerformancePage = lazyNamed(() => import("@/components/portal/VendorPerformancePage"), "VendorPerformancePage");
const VendorCoveragePage = lazyNamed(() => import("@/components/portal/VendorCoveragePage"), "VendorCoveragePage");
const SharedTasksPage = lazyNamed(() => import("@/components/portal/SharedTasksPage"), "SharedTasksPage");
const SharedTaskDetailPage = lazyNamed(() => import("@/components/portal/SharedTaskDetailPage"), "SharedTaskDetailPage");
const SharedDocumentsPage = lazyNamed(() => import("@/components/portal/SharedDocumentsPage"), "SharedDocumentsPage");
const SharedDocumentDetailPage = lazyNamed(() => import("@/components/portal/SharedDocumentDetailPage"), "SharedDocumentDetailPage");
const SharedNotificationsPage = lazyNamed(() => import("@/components/portal/SharedNotificationsPage"), "SharedNotificationsPage");
const SharedNotificationDetailPage = lazyNamed(() => import("@/components/portal/SharedNotificationDetailPage"), "SharedNotificationDetailPage");
const SharedActivitiesPage = lazyNamed(() => import("@/components/portal/SharedActivitiesPage"), "SharedActivitiesPage");
const SharedActivityDetailPage = lazyNamed(() => import("@/components/portal/SharedActivityDetailPage"), "SharedActivityDetailPage");
const EventsPage = lazyNamed(() => import("@/components/portal/EventsPage"), "EventsPage");
const EventExecutionPage = lazyNamed(() => import("@/components/portal/EventExecutionPage"), "EventExecutionPage");
const EventIssuesPage = lazyNamed(() => import("@/components/portal/EventIssuesPage"), "EventIssuesPage");
const SettlementsPage = lazyNamed(() => import("@/components/portal/SettlementsPage"), "SettlementsPage");
const SettlementsCasesPage = lazyNamed(() => import("@/components/portal/SettlementsCasesPage"), "SettlementsCasesPage");
const PayoutQueuePage = lazyNamed(() => import("@/components/portal/PayoutQueuePage"), "PayoutQueuePage");
const SettlementDetailPage = lazyNamed(() => import("@/components/portal/SettlementDetailPage"), "SettlementDetailPage");
const ReportsHomePage = lazyNamed(() => import("@/components/portal/ReportsHomePage"), "ReportsHomePage");
const ReportsAcquisitionPage = lazyNamed(() => import("@/components/portal/ReportsAcquisitionPage"), "ReportsAcquisitionPage");
const ReportsRmPage = lazyNamed(() => import("@/components/portal/ReportsRmPage"), "ReportsRmPage");
const ReportsVendorPerformancePage = lazyNamed(() => import("@/components/portal/ReportsVendorPerformancePage"), "ReportsVendorPerformancePage");
const ReportsVendorCoveragePage = lazyNamed(() => import("@/components/portal/ReportsVendorCoveragePage"), "ReportsVendorCoveragePage");
const ReportsEventsPage = lazyNamed(() => import("@/components/portal/ReportsEventsPage"), "ReportsEventsPage");
const ReportsSettlementsPage = lazyNamed(() => import("@/components/portal/ReportsSettlementsPage"), "ReportsSettlementsPage");
const ReportsFinancePage = lazyNamed(() => import("@/components/portal/ReportsFinancePage"), "ReportsFinancePage");
const ParityPlaceholderPage = lazyNamed(() => import("@/components/portal/ParityPlaceholderPage"), "ParityPlaceholderPage");

function LoadingState() {
  return (
    <div className="screen-center" data-testid="app-loading-screen">
      <div className="loading-ring" />
      <p className="screen-copy">Loading secure workspace…</p>
    </div>
  );
}

function ProtectedRoute({ ready, user, children }) {
  if (!ready) {
    return <LoadingState />;
  }

  if (!user) {
    return <Navigate replace to="/login" />;
  }

  return children;
}

function AppErrorFallback() {
  return (
    <div className="screen-center" data-testid="app-error-fallback">
      <div className="page-stack" style={{ maxWidth: 520, textAlign: "left" }}>
        <h2>Workspace temporarily unavailable</h2>
        <p className="screen-copy">
          An unexpected application error was captured. Refresh the page or contact an administrator if the problem
          continues.
        </p>
        <Button className="gold-button" onClick={() => window.location.reload()}>
          Reload portal
        </Button>
      </div>
    </div>
  );
}

function RouteSuspense({ children }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

function AppRoutes({ installUi }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [passwordResetForm, setPasswordResetForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [passwordResetSaving, setPasswordResetSaving] = useState(false);

  const refreshSession = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data.user);
      return response.data.user;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const hydrate = async () => {
      const currentUser = await refreshSession();
      if (mounted) {
        setUser(currentUser);
        setReady(true);
      }
    };

    hydrate().catch((error) => {
      captureFrontendException(error, { source: "initial-session-hydration" });
      if (mounted) {
        setReady(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, [refreshSession]);

  const handleLogin = async (credentials) => {
    const response = await api.post("/auth/login", credentials);
    setUser(response.data.user);
    toast.success("Secure session established", { id: "login-success" });
    return response.data.user;
  };

  const handleBootstrapComplete = async () => {
    const currentUser = await refreshSession();
    if (currentUser) {
      toast.success("Initial administrator created", { id: "bootstrap-success" });
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore logout errors and clear local session state
    } finally {
      setUser(null);
      toast.success("Signed out from Team Portal", { id: "logout-success" });
    }
  };

  const handleSessionExpired = () => {
    setUser(null);
    toast.error("Your session expired. Please sign in again.", { id: "session-expired" });
  };

  const handleFirstLoginReset = async () => {
    if (passwordResetForm.new_password !== passwordResetForm.confirm_password) {
      toast.error("New password and confirmation must match.", { id: "first-login-reset-mismatch" });
      return;
    }
    setPasswordResetSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: passwordResetForm.current_password,
        new_password: passwordResetForm.new_password,
      });
      const refreshed = await refreshSession();
      setUser(refreshed);
      setPasswordResetForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password updated. UAT account is now ready.", { id: "first-login-reset-success" });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to update password.", { id: "first-login-reset-error" });
    } finally {
      setPasswordResetSaving(false);
    }
  };

  const renderLazyRoute = (Component, props = {}) => (
    <RouteSuspense>
      <Component onSessionExpired={handleSessionExpired} user={user} {...props} />
    </RouteSuspense>
  );

  const landingPath = user ? getDefaultLandingPath(user.role) : "/login";

  const realRoutes = [
    {
      path: "/overview",
      element: renderLazyRoute(OverviewPage),
    },
    {
      path: "/schedule",
      element: renderLazyRoute(ScheduleHubPage),
    },
    {
      path: "/venus/operations",
      element: renderLazyRoute(VenUsOperationsPage),
    },
    {
      path: "/acquisition",
      element: renderLazyRoute(AcquisitionPage),
    },
    {
      path: "/acquisition/pipeline",
      element: renderLazyRoute(AcquisitionPipelinePage),
    },
    {
      path: "/acquisition/brief",
      element: renderLazyRoute(AcquisitionBriefPage),
    },
    {
      path: "/acquisition/visit-prep",
      element: renderLazyRoute(AcquisitionVisitPrepPage),
    },
    {
      path: "/acquisition/capture/:leadId",
      element: renderLazyRoute(AcquisitionFieldCapturePage),
    },
    {
      path: "/acquisition/capture/new",
      element: renderLazyRoute(AcquisitionFieldCapturePage),
    },
    {
      path: "/acquisition/review",
      element: renderLazyRoute(AcquisitionReviewPage),
    },
    {
      path: "/rm",
      element: renderLazyRoute(RmPage),
    },
    {
      path: "/rm/pipeline",
      element: renderLazyRoute(RmPipelinePage),
    },
    {
      path: "/rm/mobile",
      element: renderLazyRoute(RmMobileDeskPage),
    },
    {
      path: "/vendors",
      element: renderLazyRoute(VendorsPage),
    },
    {
      path: "/supply/connections",
      element: renderLazyRoute(VenueVendorConnectionsPage),
    },
    {
      path: "/vendors/directory",
      element: renderLazyRoute(VendorsDirectoryPage),
    },
    {
      path: "/vendors/performance",
      element: renderLazyRoute(VendorPerformancePage),
    },
    {
      path: "/vendors/coverage",
      element: renderLazyRoute(VendorCoveragePage),
    },
    {
      path: "/work/tasks",
      element: renderLazyRoute(SharedTasksPage),
    },
    {
      path: "/work/tasks/:taskId",
      element: renderLazyRoute(SharedTaskDetailPage),
    },
    {
      path: "/work/documents",
      element: renderLazyRoute(SharedDocumentsPage),
    },
    {
      path: "/work/documents/:documentId",
      element: renderLazyRoute(SharedDocumentDetailPage),
    },
    {
      path: "/work/notifications",
      element: renderLazyRoute(SharedNotificationsPage),
    },
    {
      path: "/work/notifications/:notificationId",
      element: renderLazyRoute(SharedNotificationDetailPage),
    },
    {
      path: "/work/activities",
      element: renderLazyRoute(SharedActivitiesPage),
    },
    {
      path: "/work/activities/:activityId",
      element: renderLazyRoute(SharedActivityDetailPage),
    },
    {
      path: "/events",
      element: renderLazyRoute(EventsPage),
    },
    {
      path: "/events/execution",
      element: renderLazyRoute(EventExecutionPage),
    },
    {
      path: "/events/issues",
      element: renderLazyRoute(EventIssuesPage),
    },
    {
      path: "/settlements",
      element: renderLazyRoute(SettlementsPage),
    },
    {
      path: "/settlements/cases",
      element: renderLazyRoute(SettlementsCasesPage),
    },
    {
      path: "/settlements/payouts",
      element: renderLazyRoute(PayoutQueuePage),
    },
    {
      path: "/settlements/events/:settlementId",
      element: renderLazyRoute(SettlementDetailPage),
    },
    {
      path: "/reports",
      element: renderLazyRoute(ReportsHomePage),
    },
    {
      path: "/reports/acquisition",
      element: renderLazyRoute(ReportsAcquisitionPage),
    },
    {
      path: "/reports/rm",
      element: renderLazyRoute(ReportsRmPage),
    },
    {
      path: "/reports/vendors/performance",
      element: renderLazyRoute(ReportsVendorPerformancePage),
    },
    {
      path: "/reports/vendors/coverage",
      element: renderLazyRoute(ReportsVendorCoveragePage),
    },
    {
      path: "/reports/events",
      element: renderLazyRoute(ReportsEventsPage),
    },
    {
      path: "/reports/settlements",
      element: renderLazyRoute(ReportsSettlementsPage),
    },
    {
      path: "/reports/finance",
      element: renderLazyRoute(ReportsFinancePage),
    },
    {
      path: "/finance",
      element: renderLazyRoute(FinancePage),
    },
    {
      path: "/payroll",
      element: renderLazyRoute(PayrollPage),
    },
    {
      path: "/hr",
      element: renderLazyRoute(HRPage),
    },
    {
      path: "/hr/employees",
      element: renderLazyRoute(HREmployeesPage),
    },
    {
      path: "/hr/hiring",
      element: renderLazyRoute(HRHiringPage),
    },
    {
      path: "/hr/requisitions",
      element: renderLazyRoute(HRRequisitionsPage),
    },
    {
      path: "/hr/requisitions/:reqId",
      element: renderLazyRoute(HRRequisitionDetailPage),
    },
    {
      path: "/hr/candidates",
      element: renderLazyRoute(HRCandidatesPage),
    },
    {
      path: "/hr/candidates/:candidateId",
      element: renderLazyRoute(HRCandidateDetailPage),
    },
    {
      path: "/hr/onboarding",
      element: renderLazyRoute(HROnboardingPage),
    },
    {
      path: "/hr/onboarding/:employeeId",
      element: renderLazyRoute(HROnboardingDetailPage),
    },
    {
      path: "/hr/exits",
      element: renderLazyRoute(HRExitsPage),
    },
    {
      path: "/hr/exits/:employeeId",
      element: renderLazyRoute(HRExitDetailPage),
    },
    {
      path: "/hr/assets",
      element: renderLazyRoute(HRAssetsPage),
    },
    {
      path: "/hr/assets/:assetId",
      element: renderLazyRoute(HRAssetDetailPage),
    },
    {
      path: "/hr/reviews",
      element: renderLazyRoute(HRReviewsPage),
    },
    {
      path: "/hr/reviews/:reviewId",
      element: renderLazyRoute(HRReviewDetailPage),
    },
    {
      path: "/hr/analytics",
      element: renderLazyRoute(HRAnalyticsPage),
    },
    {
      path: "/chat/shared",
      element: renderLazyRoute(SharedChatPage, { title: "Shared Team Chat" }),
    },
    {
      path: "/poc/shared-chat",
      element: renderLazyRoute(SharedChatPage, { title: "POC · Authenticated Shared Team Chat" }),
    },
  ];

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage onBootstrapComplete={handleBootstrapComplete} onLogin={handleLogin} />} />
        <Route path="/owner-onboarding/:token" element={<RouteSuspense><OwnerOnboardingPage /></RouteSuspense>} />
        <Route path="/" element={<Navigate replace to={landingPath} />} />
        <Route
          path="/"
          element={
            <ProtectedRoute ready={ready} user={user}>
              <PortalShell installUi={installUi} onLogout={handleLogout} user={user} />
            </ProtectedRoute>
          }
        >
          {realRoutes.map((route) => (
            <Route
              key={route.path}
              element={<ModuleAccessGuard routePath={route.path} user={user}>{route.element}</ModuleAccessGuard>}
              path={route.path.replace(/^\//, "")}
            />
          ))}
          {PLACEHOLDER_ROUTE_IDS.map((routeId) => {
            const route = getRouteById(routeId);
            return (
              <Route
                key={route.id}
                element={<ModuleAccessGuard routePath={route.path} user={user}><RouteSuspense><ParityPlaceholderPage routeId={route.id} user={user} /></RouteSuspense></ModuleAccessGuard>}
                path={route.path.replace(/^\//, "")}
              />
            );
          })}
          <Route path="*" element={<Navigate replace to={landingPath} />} />
        </Route>
        <Route path="*" element={<Navigate replace to={landingPath} />} />
      </Routes>

      <Dialog open={Boolean(user?.must_reset_password)}>
        <DialogContent className="dialog-surface" data-testid="first-login-reset-dialog">
          <DialogHeader>
            <DialogTitle>Change temporary password</DialogTitle>
            <DialogDescription>This account was created with a temporary password for controlled UAT/demo use. Set a new password before continuing.</DialogDescription>
          </DialogHeader>
          <div className="page-stack">
            <Input data-testid="first-login-current-password-input" placeholder="Current temporary password" type="password" value={passwordResetForm.current_password} onChange={(event) => setPasswordResetForm((current) => ({ ...current, current_password: event.target.value }))} />
            <Input data-testid="first-login-new-password-input" placeholder="New password" type="password" value={passwordResetForm.new_password} onChange={(event) => setPasswordResetForm((current) => ({ ...current, new_password: event.target.value }))} />
            <Input data-testid="first-login-confirm-password-input" placeholder="Confirm new password" type="password" value={passwordResetForm.confirm_password} onChange={(event) => setPasswordResetForm((current) => ({ ...current, confirm_password: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button className="gold-button" data-testid="first-login-reset-submit-button" disabled={passwordResetSaving} onClick={handleFirstLoginReset} type="button">{passwordResetSaving ? "Updating…" : "Update password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);

  useEffect(() => {
    const handlePrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    const handleInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const outcome = await deferredPrompt.userChoice;
    if (outcome?.outcome === 'accepted') {
      setInstalled(true);
      setDeferredPrompt(null);
      return true;
    }
    return false;
  };

  return { canInstall: Boolean(deferredPrompt) && !installed, installed, promptInstall };
}

function usePwaUpdatePrompt() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [activeBuildId, setActiveBuildId] = useState(window.__VENULOQ_BUILD_ID__ || 'static');

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event) => {
      if (event.data?.type === 'PWA_BUILD_ACTIVATED') {
        setActiveBuildId(event.data.buildId);
      }
    };

    const onControllerChange = () => {
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    const checkForUpdate = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
          if (registration.waiting) {
            setWaitingWorker(registration.waiting);
            setUpdateReady(true);
          }
          registration.addEventListener('updatefound', () => {
            const installing = registration.installing;
            if (!installing) return;
            installing.addEventListener('statechange', () => {
              if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                setWaitingWorker(registration.waiting || installing);
                setUpdateReady(true);
              }
            });
          });
        }
      } catch {
        // best-effort only
      }
    };

    // Check immediately on mount
    checkForUpdate();

    // Then check every 60 seconds
    const interval = setInterval(checkForUpdate, 60000);

    return () => {
      clearInterval(interval);
      navigator.serviceWorker.removeEventListener('message', onMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const applyUpdate = () => {
    if (!waitingWorker) return;
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  };

  return { activeBuildId, applyUpdate, updateReady };
}

export default function App() {
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const { activeBuildId, applyUpdate, updateReady } = usePwaUpdatePrompt();
  useEffect(() => {
    document.documentElement.classList.add("dark");
    initFrontendMonitoring();
  }, []);

  useEffect(() => {
    document.body.dataset.displayMode = installed ? 'standalone' : 'browser';
  }, [installed]);

  const installUi = useMemo(() => ({ canInstall, installed, promptInstall }), [canInstall, installed, promptInstall]);

  const content = (
    <div className="app-frame vlq-noise">
      {updateReady ? (
        <div className="pwa-update-banner" data-testid="pwa-update-banner">
          <div>
            <strong>Update available</strong>
            <span>A newer VenuLoQ build is ready. Refresh when you’re ready to apply it.</span>
          </div>
          <Button className="gold-button compact-button" data-testid="pwa-update-apply-button" onClick={applyUpdate}>Refresh app</Button>
        </div>
      ) : null}
      <BrowserRouter>
        <AppRoutes installUi={installUi} />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );

  return (
    <ThemeProvider attribute="class" forcedTheme="dark">
      {sentryEnabled ? <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>{content}</Sentry.ErrorBoundary> : content}
    </ThemeProvider>
  );
}
